import { useTheme } from "next-themes";
import { Toaster as Sonner, toast as sonnerToast, ExternalToast } from "sonner";
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { Capacitor } from '@capacitor/core';

type ToasterProps = React.ComponentProps<typeof Sonner>;

// Haptic feedback helper for toast dismissal
const triggerDismissHaptic = async () => {
  if (Capacitor.isNativePlatform()) {
    try {
      await Haptics.impact({ style: ImpactStyle.Light });
    } catch (error) {
      console.warn('Haptics not available:', error);
    }
  } else if (navigator.vibrate) {
    navigator.vibrate(10);
  }
};

// Wrapper for toast that adds haptic feedback on dismiss
type ToastMessage = Parameters<typeof sonnerToast>[0];
type ToastOptions = ExternalToast;

const createToastWithHaptics = () => {
  const wrappedToast = (message: ToastMessage, options?: ToastOptions) => {
    return sonnerToast(message, {
      ...options,
      onDismiss: (t) => {
        triggerDismissHaptic();
        options?.onDismiss?.(t);
      },
    });
  };

  // Copy all methods from original toast
  wrappedToast.success = (message: ToastMessage, options?: ToastOptions) =>
    sonnerToast.success(message, { ...options, onDismiss: (t) => { triggerDismissHaptic(); options?.onDismiss?.(t); } });
  
  wrappedToast.error = (message: ToastMessage, options?: ToastOptions) =>
    sonnerToast.error(message, { ...options, onDismiss: (t) => { triggerDismissHaptic(); options?.onDismiss?.(t); } });
  
  wrappedToast.warning = (message: ToastMessage, options?: ToastOptions) =>
    sonnerToast.warning(message, { ...options, onDismiss: (t) => { triggerDismissHaptic(); options?.onDismiss?.(t); } });
  
  wrappedToast.info = (message: ToastMessage, options?: ToastOptions) =>
    sonnerToast.info(message, { ...options, onDismiss: (t) => { triggerDismissHaptic(); options?.onDismiss?.(t); } });
  
  wrappedToast.loading = (message: ToastMessage, options?: ToastOptions) =>
    sonnerToast.loading(message, { ...options, onDismiss: (t) => { triggerDismissHaptic(); options?.onDismiss?.(t); } });
  
  wrappedToast.dismiss = sonnerToast.dismiss;
  wrappedToast.promise = sonnerToast.promise;
  wrappedToast.custom = sonnerToast.custom;
  wrappedToast.message = sonnerToast.message;

  return wrappedToast;
};

const toast = createToastWithHaptics();

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme();

  return (
    <Sonner
      theme={theme as ToasterProps["theme"]}
      className="toaster group"
      position="top-center"
      swipeDirections={["top"]}
      toastOptions={{
        classNames: {
          toast:
            "group toast group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-lg group-[.toaster]:cursor-grab active:group-[.toaster]:cursor-grabbing",
          description: "group-[.toast]:text-muted-foreground",
          actionButton: "group-[.toast]:bg-primary group-[.toast]:text-primary-foreground",
          cancelButton: "group-[.toast]:bg-muted group-[.toast]:text-muted-foreground",
        },
      }}
      {...props}
    />
  );
};

export { Toaster, toast };
